/**
 * Testimonials Slider using Swiper
 * Shows configurable slides per viewport on desktop (1200px+), 1 slide on tablets and mobile (< 1200px)
 * Mobile: init after first slide image loads to avoid blocking LCP.
 */

function initTestimonialsSwiperWhenReady(element, callback) {
  const firstSlide = element.querySelector('.swiper-slide');
  const img = firstSlide?.querySelector('img');
  const timeout = 3000;
  if (!img || img.complete) {
    callback();
    return;
  }
  const done = () => {
    img.removeEventListener('load', onDone);
    img.removeEventListener('error', onDone);
    clearTimeout(timer);
    callback();
  };
  const onDone = () => done();
  img.addEventListener('load', onDone, { once: true });
  img.addEventListener('error', onDone, { once: true });
  const timer = setTimeout(done, timeout);
}

function initTestimonialsSwiper(element) {
  // Get slides per view from data attribute, default to 2
  const slidesPerViewDesktop = parseInt(element.getAttribute('data-slides-per-view')) || 2;
  
  const swiper = new Swiper(element, {
    slidesPerView: 1.12,
    spaceBetween: 12,
    loop: false,
    navigation: {
      nextEl: (element.closest('.testimonials__slider-wrapper') || element).querySelector('.swiper-button-next'),
      prevEl: (element.closest('.testimonials__slider-wrapper') || element).querySelector('.swiper-button-prev'),
    },
    pagination: {
      el: (element.closest('.testimonials__slider-wrapper') || element).querySelector('.testimonials__dots'),
      clickable: true,
    },
    breakpoints: {
      // Mobile: 1 slide
      320: {
        slidesPerView: 1.12,
        spaceBetween: 12,
      },
      // Tablet: 1 slide (arrows show from 750px)
      750: {
        slidesPerView: 1,
        spaceBetween: 24,
      },
      // Desktop (1200px and above): configurable slides per view
      1200: {
        slidesPerView: slidesPerViewDesktop,
        spaceBetween: 30,
      },
    },
    // Touch/swipe support
    touchEventsTarget: 'container',
    touchRatio: 1,
    touchAngle: 45,
    grabCursor: true,
    // Prevent slide change on click - only allow navigation via arrows
    simulateTouch: false,
    allowTouchMove: true,
    // Disable slide change on click
    slideToClickedSlide: false,
    // Update navigation on slide change
    on: {
      slideChange: function() {
        updateArrowVisibility(this);
      },
      init: function() {
        updateArrowVisibility(this);
        // Prevent clicks on video elements from changing slides
        preventVideoClicksFromChangingSlides(this);
      },
      resize: function() {
        updateArrowVisibility(this);
      },
    },
  });
  
  return swiper;
}

/**
 * Prevents clicks on video elements from triggering slide changes
 */
function preventVideoClicksFromChangingSlides(swiper) {
  const slides = swiper.slides;
  
  slides.forEach((slide) => {
    // Find all video-related elements in the slide
    const videoWrappers = slide.querySelectorAll('.testimonials__video-wrapper');
    const deferredMedia = slide.querySelectorAll('deferred-media');
    const videos = slide.querySelectorAll('video');
    const iframes = slide.querySelectorAll('iframe');
    
    // Combine all video-related elements
    const videoElements = [
      ...videoWrappers,
      ...deferredMedia,
      ...videos,
      ...iframes
    ];
    
    // Stop all events on video elements
    const stopPropagation = (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    
    videoElements.forEach((videoEl) => {
      videoEl.addEventListener('click', stopPropagation, true); // Use capture phase
      videoEl.addEventListener('mousedown', stopPropagation, true);
      videoEl.addEventListener('mouseup', stopPropagation, true);
      videoEl.addEventListener('touchstart', stopPropagation, true);
      videoEl.addEventListener('touchend', stopPropagation, true);
    });
    
    // Prevent clicks on video controls - especially important for mute/unmute button
    videos.forEach((video) => {
      // Setup function to prevent clicks on all video control elements
      const setupVideoControls = () => {
        // Use MutationObserver to catch dynamically added controls
        const observer = new MutationObserver(() => {
          // Get all elements within the video (controls are rendered inside)
          const allElements = video.querySelectorAll('*');
          allElements.forEach((element) => {
            // Stop all events on control elements
            const stopAll = (e) => {
              e.stopPropagation();
              e.stopImmediatePropagation();
            };
            element.addEventListener('click', stopAll, true);
            element.addEventListener('mousedown', stopAll, true);
            element.addEventListener('mouseup', stopAll, true);
          });
        });
        
        // Observe the video element for changes (when controls are added)
        observer.observe(video, {
          childList: true,
          subtree: true
        });
        
        // Also directly add listeners to any existing controls
        const allElements = video.querySelectorAll('*');
        allElements.forEach((element) => {
          const stopAll = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
          };
          element.addEventListener('click', stopAll, true);
          element.addEventListener('mousedown', stopAll, true);
          element.addEventListener('mouseup', stopAll, true);
        });
      };
      
      // Setup immediately
      setupVideoControls();
      
      // Also setup when video loads and controls are rendered
      video.addEventListener('loadedmetadata', setupVideoControls);
      video.addEventListener('canplay', setupVideoControls);
      video.addEventListener('loadeddata', setupVideoControls);
      
      // Also listen directly on the video element for any clicks
      video.addEventListener('click', (e) => {
        // If clicking anywhere on or in the video, stop propagation
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
    });
    
    // Prevent clicks on any buttons within video wrapper
    const videoButtons = slide.querySelectorAll('.testimonials__video-wrapper button');
    videoButtons.forEach((button) => {
      button.addEventListener('click', stopPropagation, true);
      button.addEventListener('mousedown', stopPropagation, true);
      button.addEventListener('mouseup', stopPropagation, true);
    });
    
    // Prevent clicks on the entire slide if it contains a video
    if (videoElements.length > 0) {
      const stopSlideClick = (e) => {
        // Check if click is on video-related elements or their children
        const target = e.target;
        const isVideoRelated = target.closest('.testimonials__video-wrapper') ||
                              target.closest('deferred-media') ||
                              target.closest('video') ||
                              target.closest('iframe') ||
                              target.closest('[class*="video"]') ||
                              target.closest('[class*="control"]') ||
                              // Check if target is inside a video element (for controls)
                              (target.closest('video') !== null) ||
                              target.tagName === 'VIDEO' ||
                              target.tagName === 'IFRAME' ||
                              target.tagName === 'BUTTON' ||
                              target.tagName === 'INPUT' ||
                              // Check for any element that might be a video control
                              (target.getAttribute && (
                                target.getAttribute('aria-label')?.toLowerCase().includes('volume') ||
                                target.getAttribute('aria-label')?.toLowerCase().includes('mute') ||
                                target.getAttribute('aria-label')?.toLowerCase().includes('sound')
                              ));
        
        if (isVideoRelated) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          // Don't prevent default to allow video controls to work
        }
      };
      
      slide.addEventListener('click', stopSlideClick, true);
      slide.addEventListener('mousedown', stopSlideClick, true);
      slide.addEventListener('mouseup', stopSlideClick, true);
    }
  });
  
  // Also prevent clicks on the swiper container when clicking on videos
  const swiperContainer = swiper.el;
  const stopSwiperClick = (e) => {
    const target = e.target;
    
    // Check if click is on any video-related element
    const isVideoRelated = target.closest('.testimonials__video-wrapper') ||
                          target.closest('deferred-media') ||
                          target.closest('video') ||
                          target.closest('iframe') ||
                          target.closest('[class*="video"]') ||
                          target.closest('[class*="control"]') ||
                          // Check if inside video element (for native controls)
                          (target.closest('video') !== null) ||
                          target.tagName === 'VIDEO' ||
                          target.tagName === 'IFRAME' ||
                          target.tagName === 'BUTTON' ||
                          target.tagName === 'INPUT' ||
                          // Check for video control elements by common attributes
                          (target.getAttribute && (
                            target.getAttribute('aria-label')?.toLowerCase().includes('volume') ||
                            target.getAttribute('aria-label')?.toLowerCase().includes('mute') ||
                            target.getAttribute('aria-label')?.toLowerCase().includes('sound') ||
                            target.getAttribute('aria-label')?.toLowerCase().includes('play') ||
                            target.getAttribute('aria-label')?.toLowerCase().includes('pause')
                          ));
    
    if (isVideoRelated) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      // Prevent Swiper from handling this click
      return false;
    }
  };
  
  swiperContainer.addEventListener('click', stopSwiperClick, true);
  swiperContainer.addEventListener('mousedown', stopSwiperClick, true);
  swiperContainer.addEventListener('mouseup', stopSwiperClick, true);
  
  // Also override Swiper's slide change behavior
  const originalSlideTo = swiper.slideTo;
  swiper.slideTo = function(index, speed, runCallbacks, internal) {
    // Only allow slide changes if not triggered by video clicks
    // This will be checked by the event handlers above
    return originalSlideTo.call(this, index, speed, runCallbacks, internal);
  };
}

function updateArrowVisibility(swiper) {
  const nextButton = swiper.navigation.nextEl;
  const prevButton = swiper.navigation.prevEl;
  
  // Check if we're at the end
  const isEnd = swiper.isEnd;
  const isBeginning = swiper.isBeginning;
  
  if (nextButton) {
    if (isEnd) {
      nextButton.classList.add('swiper-button-disabled');
    } else {
      nextButton.classList.remove('swiper-button-disabled');
    }
  }
  
  if (prevButton) {
    if (isBeginning) {
      prevButton.classList.add('swiper-button-disabled');
    } else {
      prevButton.classList.remove('swiper-button-disabled');
    }
  }
}

/**
 * Pauses and mutes all videos except the specified one
 */
function pauseAllOtherVideos(currentVideoWrapper, sectionId) {
  const allVideoWrappers = document.querySelectorAll(`#${sectionId} .testimonials__video-wrapper`);
  
  allVideoWrappers.forEach((wrapper) => {
    if (wrapper === currentVideoWrapper) return; // Skip the current video
    
    const deferredMedia = wrapper.querySelector('deferred-media');
    if (!deferredMedia) return;
    
    const iframe = deferredMedia.querySelector('iframe');
    const video = deferredMedia.querySelector('video');
    const playButton = wrapper.querySelector('.testimonials__video-play-button');
    
    // Pause and mute iframe videos
    if (iframe) {
      const videoHost = iframe.getAttribute('data-video-type') || (iframe.src.includes('youtube.com') ? 'youtube' : 'vimeo');
      if (videoHost === 'youtube') {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        iframe.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
      } else if (videoHost === 'vimeo') {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
      }
    }
    
    // Pause and mute HTML5 videos
    if (video) {
      video.pause();
      video.muted = true;
      video.currentTime = 0; // Reset to beginning
    }
  });
}


/**
 * Auto-load videos that have preview images (so they autoplay)
 */
function autoLoadVideosWithPreview(sectionId) {
  const videoWrappers = document.querySelectorAll(`#${sectionId} .testimonials__video-wrapper`);
  
  videoWrappers.forEach((wrapper) => {
    const deferredMedia = wrapper.querySelector('deferred-media');
    if (!deferredMedia) {
      console.warn('No deferred-media found in video wrapper');
      return;
    }
    
    // Check if video has a preview image (poster button) and video is in template
    const hasPosterButton = deferredMedia.querySelector('.deferred-media__poster-button');
    const hasTemplate = deferredMedia.querySelector('template');
    const isLoaded = deferredMedia.getAttribute('data-media-loaded');
    const hasDirectVideo = deferredMedia.querySelector('iframe, video');
    
    // If video is already loaded directly, ensure it's muted
    if (hasDirectVideo) {
      const video = deferredMedia.querySelector('video');
      const iframe = deferredMedia.querySelector('iframe');
      
      // Ensure HTML5 videos are muted
      if (video) {
        video.muted = true;
      }
      
      // Ensure iframes are muted (they should already be from the URL params)
      if (iframe) {
        const src = iframe.src;
        if (src.includes('youtube.com') && !src.includes('mute=1')) {
          // Force mute for YouTube
          iframe.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
        } else if (src.includes('vimeo.com') && !src.includes('muted=1')) {
          // Force mute for Vimeo
          iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
        }
      }
      return;
    }
    
    // If there's a preview image and video is in template, auto-load it
    if (hasPosterButton && hasTemplate && !isLoaded) {
      // Load the video from template to start autoplay
      if (deferredMedia.loadContent) {
        deferredMedia.loadContent(false);
      } else {
        // Fallback: manually load from template
        const template = deferredMedia.querySelector('template');
        if (template) {
          const content = template.content.firstElementChild?.cloneNode(true);
          if (content) {
            // Ensure video is muted before adding
            if (content.tagName === 'VIDEO') {
              content.muted = true;
            }
            
            deferredMedia.setAttribute('data-media-loaded', 'true');
            deferredMedia.appendChild(content);
            
            // Hide the poster button
            const posterButton = deferredMedia.querySelector('.deferred-media__poster-button');
            if (posterButton) {
              posterButton.classList.add('deferred-media__playing');
            }
          }
        }
      }
      
      // After loading, ensure video is muted
      setTimeout(() => {
        const video = deferredMedia.querySelector('video');
        const iframe = deferredMedia.querySelector('iframe');
        
        if (video) {
          video.muted = true;
        }
        
        if (iframe) {
          const src = iframe.src;
          if (src.includes('youtube.com')) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
          } else if (src.includes('vimeo.com')) {
            iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
          }
        }
      }, 100);
    }
  });
}

/**
 * Initialize video pause listeners to ensure videos mute when paused
 */
function initVideoPauseHandlers(sectionId) {
  const videoWrappers = document.querySelectorAll(`#${sectionId} .testimonials__video-wrapper`);
  
  videoWrappers.forEach((videoWrapper) => {
    const deferredMedia = videoWrapper.querySelector('deferred-media');
    if (!deferredMedia) return;
    
    // Listen for video pause/end events
    const video = deferredMedia.querySelector('video');
    if (video) {
      // Listen for pause event (when user clicks pause in controls or video pauses)
      let pauseHandled = false;
      const handlePause = () => {
        if (pauseHandled) return;
        pauseHandled = true;
        
        // Force pause first (in case it's still playing)
        if (!video.paused) {
          video.pause();
        }
        
        // Immediately mute when paused - this is critical to stop sound
        video.muted = true;
        
        // Reset to beginning
        video.currentTime = 0;
        
        // Reset flag after a moment
        setTimeout(() => {
          pauseHandled = false;
        }, 200);
      };
      
      // Add multiple listeners to catch pause in all scenarios
      video.addEventListener('pause', handlePause, true); // Capture phase
      video.addEventListener('pause', handlePause, false); // Bubble phase
      
      // Also intercept volume changes to prevent unmuting when paused
      video.addEventListener('volumechange', () => {
        if (video.paused && !video.muted) {
          // If paused but unmuted, mute it immediately
          video.muted = true;
        }
      });
      
      // Listen for ended event
      video.addEventListener('ended', () => {
        video.pause();
        video.muted = true;
        video.currentTime = 0;
      });
      
      // Check frequently if video is paused but not muted (safety check)
      const pauseCheckInterval = setInterval(() => {
        if (video.paused) {
          // If paused, ensure it's muted
          if (!video.muted) {
            video.muted = true;
          }
          // Reset to beginning if not at start
          if (video.currentTime > 0.5) {
            video.currentTime = 0;
          }
        }
      }, 50); // Check every 50ms for immediate response
      
      // Store interval for cleanup
      if (!video.dataset.pauseCheckInterval) {
        video.dataset.pauseCheckInterval = pauseCheckInterval;
      }
    }
    
    // For iframes, we need to check periodically or use postMessage listeners
    const iframe = deferredMedia.querySelector('iframe');
    if (iframe) {
      // Create a unique handler for this iframe
      const iframePauseHandler = (event) => {
        // Only process messages from our iframe
        if (event.source !== iframe.contentWindow) return;
        
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          // Handle YouTube pause event
          if (data.event === 'onStateChange') {
            // YouTube state: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
            if (data.info === 2 || data.info === 0) { // Paused or ended
              const videoHost = iframe.getAttribute('data-video-type') || 'youtube';
              if (videoHost === 'youtube') {
                // Mute and reset to beginning
                iframe.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
                iframe.contentWindow?.postMessage('{"event":"command","func":"seekTo","args":[0,true]}', '*');
              }
            }
          }
          
          // Handle Vimeo pause event
          if (data.event === 'pause' || data.method === 'pause' || (data.event === 'playProgress' && data.paused)) {
            const videoHost = iframe.getAttribute('data-video-type') || 'vimeo';
            if (videoHost === 'vimeo') {
              // Mute and reset
              iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
              iframe.contentWindow?.postMessage('{"method":"setCurrentTime","value":0}', '*');
            }
          }
        } catch (e) {
          // Not a JSON message, ignore
        }
      };
      
      // Listen for messages from iframe
      window.addEventListener('message', iframePauseHandler);
    }
  });
}

/**
 * Ensure all autoplaying videos are muted
 */
function ensureVideosAreMuted(sectionId) {
  const videoWrappers = document.querySelectorAll(`#${sectionId} .testimonials__video-wrapper`);
  
  videoWrappers.forEach((wrapper) => {
    const deferredMedia = wrapper.querySelector('deferred-media');
    if (!deferredMedia) return;
    
    // Check for HTML5 video elements
    const videos = deferredMedia.querySelectorAll('video');
    videos.forEach((video) => {
      if (video.hasAttribute('autoplay')) {
        video.muted = true;
        // Re-apply muted if video tries to unmute
        video.addEventListener('volumechange', () => {
          if (!video.muted && video.paused) {
            video.muted = true;
          }
        });
      }
    });
    
    // Check for iframes (YouTube/Vimeo)
    const iframes = deferredMedia.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      const src = iframe.src;
      if (src.includes('autoplay=1')) {
        // Ensure muted for YouTube
        if (src.includes('youtube.com')) {
          if (!src.includes('mute=1')) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*');
          }
        }
        // Ensure muted for Vimeo
        if (src.includes('vimeo.com')) {
          if (!src.includes('muted=1')) {
            iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
          }
        }
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const swiperElements = document.querySelectorAll('[data-testimonials-swiper]');
  swiperElements.forEach((element) => {
    initTestimonialsSwiperWhenReady(element, () => {
      const swiper = initTestimonialsSwiper(element);
      const sectionId = element.getAttribute('data-testimonials-swiper');
      if (sectionId) {
        initVideoPauseHandlers(sectionId);
        setTimeout(() => ensureVideosAreMuted(sectionId), 500);
      }
    });
  });
});

// Also initialize for dynamically loaded content
if (document.readyState !== 'loading') {
  const swiperElements = document.querySelectorAll('[data-testimonials-swiper]');
  swiperElements.forEach((element) => {
    let swiper = element.swiper;
    if (!swiper) {
      initTestimonialsSwiperWhenReady(element, () => {
        swiper = initTestimonialsSwiper(element);
        const sectionId = element.getAttribute('data-testimonials-swiper');
        if (sectionId) {
          initVideoPauseHandlers(sectionId);
          setTimeout(() => {
            ensureVideosAreMuted(sectionId);
            if (element.swiper) preventVideoClicksFromChangingSlides(element.swiper);
          }, 500);
        }
      });
    }
  });
}
